<template>
  <div class="container-fluid py-1">
    <div class="row">
      <div class="col-12">
        <div class="card my-4" style="min-height: 500px; display: flex; flex-direction: column">
          <!-- Card Header -->
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div class="bg-gradient-success shadow-success border-radius-lg pt-2 pb-3">
              <div class="d-flex justify-content-between align-items-center px-3">
                <div>
                  <h6 class="text-white text-capitalize mb-1">
                    User Management
                  </h6>
                  <p class="text-white text-sm mb-0 opacity-8">
                    Manage system users and their roles
                  </p>
                </div>
                <material-button variant="gradient" color="light" size="md" @click="openAddUserModal" class="mb-0"
                  v-if="currentUserRole !== 3">
                  <i class="fas fa-plus me-2"></i>
                  Add New User
                </material-button>
              </div>
            </div>
          </div>

          <!-- Filters Section -->
          <div class="card-body px-0 pb-2" style="flex: 1">
            <div class="px-4 pb-3">
              <div class="row g-3 align-items-end">
                <!-- Search Bar -->
                <div class="col-md-5">
                  <label class="form-label text-xs text-secondary mb-1">Search Users</label>
                  <div class="filter-input-wrapper">
                    <input type="text" class="form-control form-control-modern"
                      placeholder="Search by name, email, or contact..." v-model="searchQuery" @input="handleSearch" />
                    <i class="fas fa-search search-icon"></i>
                  </div>
                </div>

                <!-- Role Filter -->
                <div class="col-md-3">
                  <label class="form-label text-xs text-secondary mb-1">Filter by Role</label>
                  <select class="form-control form-control-modern" v-model="roleFilter" @change="applyRoleFilter">
                    <option value="all">All Roles</option>
                    <option value="1">Administrator</option>
                    <option value="2">Manager</option>
                    <option value="3">Staff</option>
                  </select>
                </div>

                <!-- Reset Button -->
                <div class="col-md-2">
                  <material-button variant="gradient" color="success" size="md" @click="resetFilters"
                    class="w-100 mb-0">
                    <i class="fas fa-redo me-2"></i>
                    Reset
                  </material-button>
                </div>
              </div>
            </div>

            <!-- Users Table -->
            <div class="table-responsive p-0">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-5">
                <div class="spinner-border text-success" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3 text-muted">Loading users...</p>
              </div>

              <!-- Error State -->
              <div v-else-if="hasError" class="text-center py-5">
                <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                <h5 class="text-warning">Failed to load users</h5>
                <p class="text-muted">Please try again later.</p>
                <material-button color="success" size="sm" @click="loadUsers">
                  <i class="fas fa-redo me-2"></i>
                  Retry
                </material-button>
              </div>

              <!-- Empty State -->
              <div v-else-if="!filteredUsers || filteredUsers.length === 0" class="text-center py-5">
                <i class="fas fa-users fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No users found</h5>
                <material-button color="success" size="sm" @click="loadUsers" class="mt-2">
                  <i class="fas fa-redo me-2"></i>
                  Refresh
                </material-button>
              </div>

              <!-- Table -->
              <table v-else class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase">#</th>
                    <th class="text-uppercase">User</th>
                    <th class="text-uppercase">Email</th>
                    <th class="text-uppercase">Contact</th>
                    <th class="text-uppercase">Role</th>
                    <th class="text-uppercase text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(user, index) in paginatedUsers" :key="user.id || index" class="table-row-hover">
                    <!-- Index Column -->
                    <td class="ps-4">
                      <p class="text-xs font-weight-bold mb-0">
                        {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                      </p>
                    </td>

                    <!-- User Column -->
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="user-icon">
                          <i class="fas fa-user"></i>
                        </div>
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">
                            {{ user.first_name }} {{ user.last_name }}
                          </h6>
                        </div>
                      </div>
                    </td>

                    <!-- Email Column -->
                    <td>
                      <p class="text-sm font-weight-bold mb-0">
                        {{ user.email || "N/A" }}
                      </p>
                    </td>

                    <!-- Contact Column -->
                    <td>
                      <p class="text-sm font-weight-bold mb-0">
                        {{ user.contact || "N/A" }}
                      </p>
                    </td>

                    <!-- Role Column -->
                    <td>
                      <span :class="getRoleBadgeClass(user.role)" class="badge badge-sm">
                        {{ getRoleName(user.role) }}
                      </span>
                    </td>

                    <!-- Actions Column -->
                    <td class="text-center">
                      <div class="d-flex justify-content-center gap-2">
                        <!-- Edit Button -->
                        <button v-if="canEditUser(user)" class="btn btn-link text-primary px-2 mb-0"
                          @click="editUser(user)" title="Edit">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button v-else class="btn btn-link text-muted px-2 mb-0" disabled title="Edit not allowed"
                          style="opacity: 0.5; cursor: not-allowed">
                          <i class="fas fa-edit"></i>
                        </button>

                        <!-- Deactivate Button (only active users) -->
                        <button v-if="canDeactivateUser(user)" class="btn btn-link text-warning px-2 mb-0"
                          @click="confirmDeactivate(user)" title="Deactivate">
                          <i class="fas fa-user-slash"></i>
                        </button>

                        <!-- Activate Button (only deactivated users) -->
                        <button v-if="canActivateUser(user)" class="btn btn-link text-success px-2 mb-0"
                          @click="confirmActivate(user)" title="Activate">
                          <i class="fas fa-user-check"></i>
                        </button>

                        <!-- Delete Button (only deactivated users) -->
                        <button v-if="canDeleteUser(user)" class="btn btn-link text-danger px-2 mb-0"
                          @click="confirmDelete(user)" title="Delete">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Card Footer - Always shown -->
          <div class="card-footer px-3" style="margin-top: auto">
            <div v-if="filteredUsers && filteredUsers.length > 0">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="mb-0 text-sm text-secondary">
                    Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                    {{
                      Math.min(currentPage * itemsPerPage, filteredUsers.length)
                    }}
                    of {{ filteredUsers.length }} entries
                  </p>
                </div>

                <!-- Material Pagination -->
                <material-pagination :color="'success'" :size="'sm'">
                  <material-pagination-item :prev="true" :disabled="currentPage === 1"
                    @click="changePage(currentPage - 1)" />

                  <material-pagination-item v-for="page in displayedPages" :key="page" :label="page.toString()"
                    :active="page === currentPage" @click="changePage(page)" />

                  <material-pagination-item :next="true" :disabled="currentPage === totalPages"
                    @click="changePage(currentPage + 1)" />
                </material-pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit User Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <div class="modal-card">
          <div class="modal-header">
            <h5 class="modal-title">
              <i :class="isEditMode ? 'fas fa-edit' : 'fas fa-user-plus'" class="me-2"></i>
              {{ isEditMode ? "Edit User" : "Add New User" }}
            </h5>
            <button class="btn-close-modal" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveUser">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">First Name *</label>
                  <input type="text" class="form-control" v-model="formData.first_name" placeholder="Enter first name"
                    required />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Last Name *</label>
                  <input type="text" class="form-control" v-model="formData.last_name" placeholder="Enter last name"
                    required />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Email Address *</label>
                <input type="email" class="form-control" v-model="formData.email" placeholder="user@example.com"
                  required :disabled="isEditMode" />
              </div>

              <div class="mb-3">
                <label class="form-label">Phone Number *</label>
                <input type="tel" class="form-control" v-model="formData.contact" placeholder="0700 000 000" required />
              </div>

              <div class="mb-3">
                <label class="form-label">Role *</label>
                <select class="form-select" v-model="formData.role" required>
                  <option value="" disabled>Select a role</option>
                  <option value="1">Administrator</option>
                  <option value="2">Manager</option>
                  <option value="3">Staff</option>
                </select>
              </div>

              <div class="d-flex justify-content-end gap-2 mt-4">
                <button type="button" class="btn btn-outline-secondary" @click="closeModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="isSaving">
                  <i v-if="isSaving" class="fas fa-spinner fa-spin me-2"></i>
                  <i v-else :class="isEditMode ? 'fas fa-save' : 'fas fa-plus'" class="me-2"></i>
                  {{ isEditMode ? "Update" : "Save" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="closeDeleteModal">
      <div class="modal-container modal-sm">
        <div class="modal-card">
          <div class="modal-header border-bottom">
            <div class="text-center w-100">
              <div class="delete-icon">
                <i class="fas fa-exclamation-circle"></i>
              </div>
              <h6 class="mt-3 mb-1">Delete User?</h6>
              <p class="text-muted mb-0" v-if="userToDelete">
                {{ userToDelete.first_name }} {{ userToDelete.last_name }}
              </p>
            </div>
          </div>
          <div class="modal-body text-center py-4">
            <p class="text-muted mb-4">
              This action cannot be undone. The user will be permanently removed
              from the system.
            </p>
            <div class="d-flex justify-content-center gap-2">
              <button class="btn btn-outline-secondary" @click="closeDeleteModal">
                Cancel
              </button>
              <button class="btn btn-danger" @click="handleDeleteUser" :disabled="isDeleting">
                <i v-if="isDeleting" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-trash me-2"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Deactivate Confirmation Modal -->
    <div v-if="showDeactivateModal" class="modal-overlay" @click.self="closeDeactivateModal">
      <div class="modal-container modal-sm">
        <div class="modal-card">
          <div class="modal-header border-bottom">
            <div class="text-center w-100">
              <div class="delete-icon" style="background-color: rgba(255, 152, 0, 0.15); color: #ff9800">
                <i class="fas fa-user-slash"></i>
              </div>
              <h6 class="mt-3 mb-1">Deactivate User?</h6>
              <p class="text-muted mb-0" v-if="userToDeactivate">
                {{ userToDeactivate.first_name }} {{ userToDeactivate.last_name }}
              </p>
            </div>
          </div>
          <div class="modal-body text-center py-4">
            <p class="text-muted mb-4">
              This user will no longer be able to log in until reactivated.
            </p>
            <div class="d-flex justify-content-center gap-2">
              <button class="btn btn-outline-secondary" @click="closeDeactivateModal">
                Cancel
              </button>
              <button class="btn btn-warning" @click="handleDeactivateUser" :disabled="isDeactivating">
                <i v-if="isDeactivating" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-user-slash me-2"></i>
                Deactivate
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Activate Confirmation Modal -->
    <div v-if="showActivateModal" class="modal-overlay" @click.self="closeActivateModal">
      <div class="modal-container modal-sm">
        <div class="modal-card">
          <div class="modal-header border-bottom">
            <div class="text-center w-100">
              <div class="delete-icon" style="background-color: rgba(76, 175, 80, 0.15); color: #4caf50">
                <i class="fas fa-user-check"></i>
              </div>
              <h6 class="mt-3 mb-1">Activate User?</h6>
              <p class="text-muted mb-0" v-if="userToActivate">
                {{ userToActivate.first_name }} {{ userToActivate.last_name }}
              </p>
            </div>
          </div>
          <div class="modal-body text-center py-4">
            <p class="text-muted mb-4">
              This user will be able to log in again.
            </p>
            <div class="d-flex justify-content-center gap-2">
              <button class="btn btn-outline-secondary" @click="closeActivateModal">
                Cancel
              </button>
              <button class="btn btn-success" @click="handleActivateUser" :disabled="isActivating">
                <i v-if="isActivating" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-user-check me-2"></i>
                Activate
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialPagination from "@/components/MaterialPagination.vue";
import MaterialPaginationItem from "@/components/MaterialPaginationItem.vue";
import axios from "axios";

export default {
  name: "Operator Users",
  components: {
    MaterialButton,
    MaterialPagination,
    MaterialPaginationItem,
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      isDeactivating: false,
      isActivating: false,
      hasError: false,
      searchQuery: "",
      showModal: false,
      showDeleteModal: false,
      showDeactivateModal: false,
      showActivateModal: false,
      isEditMode: false,
      userToDelete: null,
      userToDeactivate: null,
      userToActivate: null,
      users: [],
      currentPage: 1,
      itemsPerPage: 10,
      roleFilter: "all",
      formData: {
        first_name: "",
        last_name: "",
        email: "",
        contact: "",
        role: "",
      },
      currentUserId: null,
      currentUserRole: null,
      currentUserData: null,
    };
  },
  computed: {
    safeUsers() {
      return this.users || [];
    },

    filteredUsers() {
      let users = [...this.safeUsers];

      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        users = users.filter((user) => {
          if (!user) return false;

          const searchFields = [
            user.first_name,
            user.last_name,
            user.email,
            user.contact,
          ];

          return searchFields.some(
            (field) => field && field.toString().toLowerCase().includes(query),
          );
        });
      }

      if (this.roleFilter && this.roleFilter !== "all") {
        const roleValue = parseInt(this.roleFilter);
        users = users.filter((user) => user && user.role === roleValue);
      }

      return users;
    },

    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredUsers.length / this.itemsPerPage),
      );
    },

    displayedPages() {
      const pages = [];
      const maxVisible = 5;

      if (this.totalPages <= 0) return [1];

      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredUsers.slice(start, end);
    },
  },
  created() {
    this.getCurrentUserInfo();
    this.loadUsers();
  },
  watch: {
    roleFilter() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
  },
  methods: {
    getCurrentUserInfo() {
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const userData = JSON.parse(userStr);
          this.currentUserData = userData;
          this.currentUserId = userData.user_id || userData.id;
          this.currentUserRole = userData.role;
          console.log("Current user loaded:", this.currentUserData);
          console.log(
            "User ID:",
            this.currentUserId,
            "Role:",
            this.currentUserRole,
          );
        } else {
          console.warn("No user data found in localStorage");
          this.currentUserId = null;
          this.currentUserRole = null;
        }
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
        this.currentUserId = null;
        this.currentUserRole = null;
      }
    },

    canEditUser(user) {
      if (!this.currentUserId || !this.currentUserRole) return false;

      if (this.currentUserRole === 3) return false;

      if (user.id === this.currentUserId) return false;

      if (this.currentUserRole === 1) {
        return user.role !== 1;
      }

      if (this.currentUserRole === 2) {
        return user.role === 3;
      }

      return false;
    },

    canDeleteUser(user) {
      if (user?.is_active) return false;

      if (!this.currentUserId || !this.currentUserRole) return false;

      if (this.currentUserRole === 3) return false;

      if (user.id === this.currentUserId || user.user_id === this.currentUserId) {
        return false;
      }

      if (this.currentUserRole === 1) {
        return user.role !== 1;
      }

      if (this.currentUserRole === 2) {
        return user.role === 3;
      }

      return false;
    },

    canDeactivateUser(user) {
      if (!user?.is_active) return false;

      if (!this.currentUserId || !this.currentUserRole) return false;

      if (this.currentUserRole === 3) return false;

      if (user.id === this.currentUserId || user.user_id === this.currentUserId) {
        return false;
      }

      if (this.currentUserRole === 1) {
        return user.role !== 1;
      }

      if (this.currentUserRole === 2) {
        return user.role === 3;
      }

      return false;
    },

    canActivateUser(user) {
      if (user?.is_active) return false;

      if (!this.currentUserId || !this.currentUserRole) return false;

      if (this.currentUserRole === 3) return false;

      if (user.id === this.currentUserId || user.user_id === this.currentUserId) {
        return false;
      }

      if (this.currentUserRole === 1) {
        return user.role !== 1;
      }

      if (this.currentUserRole === 2) {
        return user.role === 3;
      }

      return false;
    },

    async loadUsers() {
      this.isLoading = true;
      this.hasError = false;
      console.log("Fetching users from API...");

      try {
        const response = await axios.get("/operator-users/");
        console.log("API Response:", response.data);

        this.users = response.data.map((user) => {
          const fullName = user.full_name || "";
          const nameParts = fullName.trim().split(/\s+/);

          const firstName =
            nameParts[0] || (user.email ? user.email.split("@")[0] : "User");
          const lastName = nameParts.slice(1).join(" ") || "";

          return {
            ...user,
            first_name: firstName,
            last_name: lastName,
          };
        });

        console.log("Number of users loaded:", this.users.length);
        console.log("Roles found:", [
          ...new Set(this.users.map((u) => u.role)),
        ]);
      } catch (error) {
        console.error("Error loading users");
        this.hasError = true;
        this.showNotification("Failed to load users", "error");
      } finally {
        this.isLoading = false;
      }
    },

    handleSearch() {
      this.currentPage = 1;
    },

    applyRoleFilter() {
      this.currentPage = 1;
      console.log("Applying role filter:", this.roleFilter);
    },

    resetFilters() {
      this.searchQuery = "";
      this.roleFilter = "all";
      this.currentPage = 1;
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },

    showNotification(message, type = "info") {
      const notification = document.createElement("div");

      // Base classes
      notification.className =
        "alert alert-dismissible fade show position-fixed";

      // Set styles based on type
      if (type === "success") {
        notification.style.background =
          "linear-gradient(310deg, #4caf50 0%, #8bc34a 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
      } else if (type === "error") {
        notification.style.background =
          "linear-gradient(310deg, #f44336 0%, #f88a82 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
      } else if (type === "warning") {
        notification.style.background =
          "linear-gradient(310deg, #ff9800 0%, #ffb74d 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
      } else {
        notification.style.background =
          "linear-gradient(310deg, #17a2b8 0%, #3dcfd3 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
      }

      // Position styles
      notification.style.top = "20px";
      notification.style.right = "20px";
      notification.style.zIndex = "9999";
      notification.style.minWidth = "300px";

      // Create text node and button to avoid HTML string issues
      const textNode = document.createTextNode(message);
      notification.appendChild(textNode);

      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.className = "btn-close";
      closeButton.style.filter = "brightness(0) invert(1)";
      closeButton.setAttribute("data-bs-dismiss", "alert");
      closeButton.setAttribute("aria-label", "Close");

      notification.appendChild(closeButton);
      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 3000);
    },

    openAddUserModal() {
      if (this.currentUserRole === 3) {
        this.showNotification("Staff cannot add new users", "error");
        return;
      }

      this.isEditMode = false;
      this.resetForm();
      this.showModal = true;
    },

    editUser(user) {
      if (!this.canEditUser(user)) {
        this.showNotification(
          "You don't have permission to edit this user",
          "error",
        );
        return;
      }

      this.isEditMode = true;
      this.formData = {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        contact: user.contact,
        role: user.role.toString(),
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.isSaving = false;
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        first_name: "",
        last_name: "",
        email: "",
        contact: "",
        role: "",
      };
    },

    async saveUser() {
      this.isSaving = true;

      try {
        if (this.isEditMode) {
          await axios.patch(
            `/operator-users/${this.formData.id}/`,
            this.formData,
          );
          this.showNotification("User updated successfully!", "success");
        } else {
          const userData = {
            ...this.formData,
            role: parseInt(this.formData.role) || 3,
          };
          await axios.post("/operator-users/", userData);
          this.showNotification("User created successfully!", "success");
        }

        await this.loadUsers();
        this.closeModal();
      } catch (error) {
        console.error("Save user error");
        this.handleError(error, "Failed to save user");
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete(user) {
      if (!this.canDeleteUser(user)) {
        this.showNotification(
          "You don't have permission to delete this user",
          "error",
        );
        return;
      }

      this.userToDelete = user;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.userToDelete = null;
      this.isDeleting = false;
    },

    closeDeactivateModal() {
      this.showDeactivateModal = false;
      this.userToDeactivate = null;
      this.isDeactivating = false;
    },

    closeActivateModal() {
      this.showActivateModal = false;
      this.userToActivate = null;
      this.isActivating = false;
    },

    confirmDeactivate(user) {
      if (!this.canDeactivateUser(user)) {
        this.showNotification(
          "You don't have permission to deactivate this user",
          "error",
        );
        return;
      }

      this.userToDeactivate = user;
      this.showDeactivateModal = true;
    },

    async handleDeactivateUser() {
      if (!this.userToDeactivate || !this.canDeactivateUser(this.userToDeactivate)) {
        this.showNotification(
          "You don't have permission to deactivate this user",
          "error",
        );
        this.closeDeactivateModal();
        return;
      }

      this.isDeactivating = true;

      try {
        await axios.post(`/deactivate-user/${this.userToDeactivate.user_id}/`);
        this.showNotification("User deactivated successfully!", "success");
        await this.loadUsers();
        this.closeDeactivateModal();
      } catch (error) {
        console.error("Deactivate user error:", error);
        this.handleError(error, "Failed to deactivate user");
      } finally {
        this.isDeactivating = false;
      }
    },

    confirmActivate(user) {
      if (!this.canActivateUser(user)) {
        this.showNotification(
          "You don't have permission to activate this user",
          "error",
        );
        return;
      }

      this.userToActivate = user;
      this.showActivateModal = true;
    },

    async handleActivateUser() {
      if (!this.userToActivate || !this.canActivateUser(this.userToActivate)) {
        this.showNotification(
          "You don't have permission to activate this user",
          "error",
        );
        this.closeActivateModal();
        return;
      }

      this.isActivating = true;

      try {
        await axios.post(`/activate-user/${this.userToActivate.user_id}/`);
        this.showNotification("User activated successfully!", "success");
        await this.loadUsers();
        this.closeActivateModal();
      } catch (error) {
        console.error("Activate user error:", error);
        this.handleError(error, "Failed to activate user");
      } finally {
        this.isActivating = false;
      }
    },

    async handleDeleteUser() {
      if (!this.userToDelete || !this.canDeleteUser(this.userToDelete)) {
        this.showNotification(
          "You don't have permission to delete this user",
          "error",
        );
        this.closeDeleteModal();
        return;
      }

      this.isDeleting = true;

      try {
        await axios.delete(`/operator-users/${this.userToDelete.id}/`);
        this.showNotification("User deleted successfully!", "success");

        await this.loadUsers();
        this.closeDeleteModal();
      } catch (error) {
        console.error("Delete user error:", error);
        this.handleError(error, "Failed to delete user");
      } finally {
        this.isDeleting = false;
      }
    },

    handleError(error, defaultMessage) {
      let errorMessage = defaultMessage;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = errors.join(", ");
      } else if (error.response?.data) {
        errorMessage = JSON.stringify(error.response.data);
      }

      this.showNotification(errorMessage, "error");
    },

    getRoleName(role) {
      const roles = {
        1: "Administrator",
        2: "Manager",
        3: "Staff",
      };
      return roles[role] || "Unknown";
    },

    getRoleBadgeClass(role) {
      const classes = {
        1: "badge bg-gradient-danger",
        2: "badge bg-gradient-warning",
        3: "badge bg-gradient-info",
      };
      return classes[role] || "badge bg-gradient-secondary";
    },
  },
};
</script>

<style scoped>
.container-fluid {
  min-height: calc(100vh - 60px);
}

.card {
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-responsive {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.text-center.py-5 {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.user-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 12px;
  background-color: #ffebee; /* Light red background */
  border-radius: 50%;
  color: #f44336; /* Red color for icon */
}

.badge {
  font-size: 0.65em;
  font-weight: 600;
  padding: 0.25em 0.6em;
}

/* Keep the header with original red gradient */
.bg-gradient-success {
  background: linear-gradient(310deg, #f44335 0%, #ef5350 100%);
}

/* Custom success notification class - only affects notifications */
.custom-notification-success {
  background: linear-gradient(310deg, #4caf50 0%, #8bc34a 100%) !important;
  color: white !important;
  border: none !important;
}

.custom-notification-success .btn-close {
  filter: brightness(0) invert(1);
}

/* Ensure these don't affect other elements */
.bg-gradient-info {
  background: linear-gradient(310deg, #17a2b8 0%, #3dcfd3 100%);
}

.bg-gradient-warning {
  background: linear-gradient(310deg, #ff9800 0%, #ffb74d 100%);
}

.bg-gradient-danger {
  background: linear-gradient(310deg, #f44336 0%, #f88a82 100%);
}

.bg-gradient-primary {
  background: linear-gradient(310deg, #2196f3 0%, #64b5f6 100%);
}

.bg-gradient-secondary {
  background: linear-gradient(310deg, #6c757d 0%, #adb5bd 100%);
}

.table-row-hover {
  transition: all 0.2s ease;
}

.table-row-hover:hover {
  background-color: rgba(76, 175, 80, 0.05);
  transform: scale(1.002);
}

.card-footer {
  margin-top: auto;
  background-color: transparent;
  border-top: 1px solid #e9ecef;
  padding: 1rem 0;
}

.shadow-success {
  box-shadow:
    0 4px 20px 0 rgba(244, 67, 53, 0.14),
    0 7px 10px -5px rgba(244, 67, 53, 0.4);
}

.btn-link {
  transition: all 0.2s ease;
}

.btn-link:hover {
  transform: scale(1.1);
}

.btn-link:active {
  transform: scale(0.95);
}

.filter-input-wrapper {
  position: relative;
}

.filter-input-wrapper .form-control-modern {
  padding-right: 2.5rem;
}

.filter-input-wrapper .search-icon {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  pointer-events: none;
  font-size: 0.875rem;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

.modal-container {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-container.modal-sm {
  max-width: 400px;
}

.modal-card {
  padding: 0;
}

.modal-header {
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  font-weight: 600;
  color: #212529;
  font-size: 1.125rem;
  margin: 0;
}

.btn-close-modal {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.btn-close-modal:hover {
  background-color: #f8f9fa;
  color: #212529;
}

.modal-body {
  padding: 1.5rem;
}

.form-control,
.form-select {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #495057;
  background-color: #fff;
  transition: all 0.2s ease;
  height: auto;
}

.form-control:focus,
.form-select:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  outline: none;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #495057;
  font-size: 0.875rem;
}

.delete-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto;
  background-color: rgba(220, 53, 69, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc3545;
  font-size: 1.25rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .user-icon {
    width: 28px;
    height: 28px;
    margin-right: 8px;
  }
}
</style>
